import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Container,
  Grid,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import axios from '../utils/Axios';
import { useAuthContext } from '../auth/useAuthContext';
import { getSymbol } from './DashboardAppPage';
import Iconify from '../components/iconify';

dayjs.extend(utc);
dayjs.extend(timezone);

// --------------- constants ---------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STATUS_LABELS = {
  APPROVED: 'Approved',
  PENDING: 'Pending',
  CANCELED: 'Canceled',
  REJECTED: 'Rejected',
  NO_SHOW: 'No Show',
  COMPLETED: 'Completed',
};

const STATUS_COLORS = {
  APPROVED: '#36B37E',
  PENDING: '#FFAB00',
  CANCELED: '#FF5630',
  REJECTED: '#B71D18',
  NO_SHOW: '#919EAB',
  COMPLETED: '#00B8D9',
};

const REVENUE_STATUSES = new Set(['APPROVED', 'COMPLETED']);

// --------------- helpers ---------------

function trendPercent(current, previous) {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// --------------- sub-components ---------------

function KPICard({ title, value, trend, icon, color }) {
  const isPositive = trend >= 0;

  return (
    <Card sx={{ p: 3, height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h3">{value}</Typography>
          {trend !== null && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
              <Iconify
                icon={isPositive ? 'eva:trending-up-fill' : 'eva:trending-down-fill'}
                sx={{ color: isPositive ? 'success.main' : 'error.main', width: 20, height: 20 }}
              />
              <Typography
                variant="subtitle2"
                sx={{ color: isPositive ? 'success.main' : 'error.main' }}
              >
                {isPositive ? '+' : ''}{trend}%
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                vs last year
              </Typography>
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(color, 0.12),
          }}
        >
          <Iconify icon={icon} sx={{ width: 28, height: 28, color }} />
        </Box>
      </Stack>
    </Card>
  );
}

function ChartCard({ title, children, sx }) {
  return (
    <Card sx={{ height: '100%', ...sx }}>
      <CardHeader title={title} sx={{ pb: 0 }} />
      <Box sx={{ p: 3, pt: 2 }}>{children}</Box>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <Container maxWidth="xl">
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Skeleton variant="text" width={120} height={40} />
        <Skeleton variant="rounded" width={100} height={36} />
      </Stack>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[0, 1, 2, 3].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Skeleton variant="rounded" height={140} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Skeleton variant="rounded" height={360} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Skeleton variant="rounded" height={360} />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Skeleton variant="rounded" height={360} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Skeleton variant="rounded" height={360} />
        </Grid>
      </Grid>
    </Container>
  );
}

function EmptyState() {
  return (
    <Card sx={{ p: 8, textAlign: 'center' }}>
      <Iconify
        icon="eva:bar-chart-outline"
        sx={{ width: 64, height: 64, color: 'text.disabled', mb: 2, mx: 'auto' }}
      />
      <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
        No bookings yet
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
        Once you start receiving bookings, your analytics will appear here.
      </Typography>
    </Card>
  );
}

// --------------- main component ---------------

const Reports = () => {
  const theme = useTheme();
  const { user } = useAuthContext();
  const tz = user?.config?.timezone || 'Europe/London';
  const currencySymbol = getSymbol((user?.config?.currency || 'usd').toUpperCase());

  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get('/bookings');
      setBookings(res.data.bookings || []);
    } catch (error) {
      console.error('Reports fetchBookings error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // --- derived data (all useMemo) ---

  const availableYears = useMemo(() => {
    const years = new Set();
    years.add(new Date().getFullYear());
    bookings.forEach((b) => {
      years.add(dayjs(b.startDate).tz(tz).year());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [bookings, tz]);

  const yearBookings = useMemo(
    () => bookings.filter((b) => dayjs(b.startDate).tz(tz).year() === selectedYear),
    [bookings, selectedYear, tz]
  );

  const prevYearBookings = useMemo(
    () => bookings.filter((b) => dayjs(b.startDate).tz(tz).year() === selectedYear - 1),
    [bookings, selectedYear, tz]
  );

  // --- KPI data ---

  const kpiData = useMemo(() => {
    const curTotal = yearBookings.length;
    const prevTotal = prevYearBookings.length;

    const curRevenueBookings = yearBookings.filter((b) => REVENUE_STATUSES.has(b.status));
    const prevRevenueBookings = prevYearBookings.filter((b) => REVENUE_STATUSES.has(b.status));
    const curRevenue = curRevenueBookings.reduce((sum, b) => sum + (b.price || 0), 0);
    const prevRevenue = prevRevenueBookings.reduce((sum, b) => sum + (b.price || 0), 0);

    const curAvg = curRevenueBookings.length > 0 ? curRevenue / curRevenueBookings.length : 0;
    const prevAvg = prevRevenueBookings.length > 0 ? prevRevenue / prevRevenueBookings.length : 0;

    const curCompleted = curRevenueBookings.length;
    const prevCompleted = prevRevenueBookings.length;
    const curRate = curTotal > 0 ? Math.round((curCompleted / curTotal) * 100) : 0;
    const prevRate = prevTotal > 0 ? Math.round((prevCompleted / prevTotal) * 100) : 0;

    return {
      totalBookings: { value: curTotal, trend: trendPercent(curTotal, prevTotal) },
      revenue: { value: curRevenue, trend: trendPercent(curRevenue, prevRevenue) },
      avgValue: { value: curAvg, trend: trendPercent(curAvg, prevAvg) },
      completionRate: { value: curRate, trend: trendPercent(curRate, prevRate) },
    };
  }, [yearBookings, prevYearBookings]);

  // --- Monthly booking counts ---

  const monthlyBookings = useMemo(() => {
    const data = new Array(12).fill(0);
    yearBookings.forEach((b) => {
      data[dayjs(b.startDate).tz(tz).month()]++;
    });
    return data;
  }, [yearBookings, tz]);

  // --- Monthly revenue (APPROVED + COMPLETED only) ---

  const monthlyRevenue = useMemo(() => {
    const data = new Array(12).fill(0);
    yearBookings
      .filter((b) => REVENUE_STATUSES.has(b.status))
      .forEach((b) => {
        data[dayjs(b.startDate).tz(tz).month()] += b.price || 0;
      });
    return data;
  }, [yearBookings, tz]);

  // --- Status distribution ---

  const statusDistribution = useMemo(() => {
    const counts = {};
    yearBookings.forEach((b) => {
      counts[b.status] = (counts[b.status] || 0) + 1;
    });
    // Only include statuses with count > 0
    const labels = [];
    const series = [];
    const colors = [];
    Object.keys(STATUS_LABELS).forEach((key) => {
      if (counts[key] > 0) {
        labels.push(STATUS_LABELS[key]);
        series.push(counts[key]);
        colors.push(STATUS_COLORS[key]);
      }
    });
    return { labels, series, colors, total: yearBookings.length };
  }, [yearBookings]);

  // --- Top services ---

  const topServices = useMemo(() => {
    const map = {};
    yearBookings.forEach((b) => {
      const name = b.service?.name || 'Unknown';
      if (!map[name]) map[name] = { count: 0, revenue: 0 };
      map[name].count++;
      if (REVENUE_STATUSES.has(b.status)) {
        map[name].revenue += b.price || 0;
      }
    });
    return Object.entries(map)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 7)
      .map(([name, data]) => ({ name, ...data }));
  }, [yearBookings]);

  // --- shared chart styling ---

  const baseChartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      grid: {
        strokeDashArray: 3,
        borderColor: theme.palette.divider,
      },
      xaxis: {
        categories: MONTHS,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: theme.palette.text.secondary, fontSize: '12px' } },
      },
      yaxis: {
        labels: { style: { colors: theme.palette.text.secondary, fontSize: '12px' } },
      },
      tooltip: { theme: 'light' },
    }),
    [theme]
  );

  // --- area chart options: bookings ---

  const bookingChartOptions = useMemo(
    () => ({
      ...baseChartOptions,
      chart: { ...baseChartOptions.chart, type: 'area', height: 320 },
      colors: [theme.palette.primary.main],
      fill: {
        type: 'gradient',
        gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] },
      },
      stroke: { curve: 'smooth', width: 2.5 },
      dataLabels: { enabled: false },
      yaxis: {
        ...baseChartOptions.yaxis,
        labels: {
          ...baseChartOptions.yaxis.labels,
          formatter: (v) => Math.round(v),
        },
      },
      tooltip: {
        ...baseChartOptions.tooltip,
        y: { formatter: (v) => `${v} bookings` },
      },
    }),
    [baseChartOptions, theme]
  );

  // --- area chart options: revenue ---

  const revenueChartOptions = useMemo(
    () => ({
      ...baseChartOptions,
      chart: { ...baseChartOptions.chart, type: 'area', height: 320 },
      colors: ['#36B37E'],
      fill: {
        type: 'gradient',
        gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] },
      },
      stroke: { curve: 'smooth', width: 2.5 },
      dataLabels: { enabled: false },
      yaxis: {
        ...baseChartOptions.yaxis,
        labels: {
          ...baseChartOptions.yaxis.labels,
          formatter: (v) => `${currencySymbol}${Math.round(v)}`,
        },
      },
      tooltip: {
        ...baseChartOptions.tooltip,
        y: { formatter: (v) => `${currencySymbol}${v.toFixed(2)}` },
      },
    }),
    [baseChartOptions, currencySymbol]
  );

  // --- donut chart options ---

  const donutOptions = useMemo(
    () => ({
      chart: { type: 'donut' },
      labels: statusDistribution.labels,
      colors: statusDistribution.colors,
      legend: {
        position: 'bottom',
        labels: { colors: theme.palette.text.secondary },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '72%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: () => statusDistribution.total,
              },
            },
          },
        },
      },
      stroke: { show: false },
      dataLabels: { enabled: false },
      tooltip: { theme: 'light' },
    }),
    [statusDistribution, theme]
  );

  // --- horizontal bar chart options ---

  const serviceBarOptions = useMemo(
    () => ({
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
      colors: [theme.palette.primary.main],
      dataLabels: { enabled: false },
      grid: { strokeDashArray: 3, borderColor: theme.palette.divider },
      xaxis: {
        categories: topServices.map((s) => s.name),
        labels: {
          style: { colors: theme.palette.text.secondary, fontSize: '12px' },
          formatter: (v) => Math.round(v),
        },
      },
      yaxis: {
        labels: { style: { colors: theme.palette.text.secondary, fontSize: '12px' } },
      },
      tooltip: {
        theme: 'light',
        custom: ({ dataPointIndex }) => {
          const svc = topServices[dataPointIndex];
          if (!svc) return '';
          return `<div style="padding:8px 12px;font-size:13px">
            <strong>${escapeHtml(svc.name)}</strong><br/>
            Bookings: ${svc.count}<br/>
            Revenue: ${escapeHtml(currencySymbol)}${svc.revenue.toFixed(2)}
          </div>`;
        },
      },
    }),
    [theme, topServices, currencySymbol]
  );

  // --------------- render ---------------

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Reports | {localStorage.getItem('companyName') ?? 'FlowBookings'}</title>
        </Helmet>
        <LoadingSkeleton />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Reports | {localStorage.getItem('companyName') ?? 'FlowBookings'}</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
          <Typography variant="h4">Reports</Typography>
          <Select
            size="small"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            sx={{ minWidth: 100 }}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        {yearBookings.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* KPI Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Total Bookings"
                  value={kpiData.totalBookings.value}
                  trend={kpiData.totalBookings.trend}
                  icon="eva:calendar-fill"
                  color={theme.palette.primary.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Revenue"
                  value={`${currencySymbol}${kpiData.revenue.value.toFixed(2)}`}
                  trend={kpiData.revenue.trend}
                  icon="eva:credit-card-fill"
                  color="#36B37E"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Avg Booking Value"
                  value={`${currencySymbol}${kpiData.avgValue.value.toFixed(2)}`}
                  trend={kpiData.avgValue.trend}
                  icon="eva:pricetags-fill"
                  color={theme.palette.warning.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Completion Rate"
                  value={`${kpiData.completionRate.value}%`}
                  trend={kpiData.completionRate.trend}
                  icon="eva:checkmark-circle-2-fill"
                  color={theme.palette.info.main}
                />
              </Grid>
            </Grid>

            {/* Row 2: Booking Trends + Status Breakdown */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={8}>
                <ChartCard title="Booking Trends">
                  <ReactApexChart
                    type="area"
                    height={320}
                    options={bookingChartOptions}
                    series={[{ name: 'Bookings', data: monthlyBookings }]}
                  />
                </ChartCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <ChartCard title="Status Breakdown">
                  {statusDistribution.series.length > 0 ? (
                    <ReactApexChart
                      type="donut"
                      height={320}
                      options={donutOptions}
                      series={statusDistribution.series}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320 }}>
                      <Typography variant="body2" color="text.disabled">
                        No bookings for {selectedYear}
                      </Typography>
                    </Box>
                  )}
                </ChartCard>
              </Grid>
            </Grid>

            {/* Row 3: Revenue Trends + Top Services */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <ChartCard title="Revenue Trends">
                  <ReactApexChart
                    type="area"
                    height={320}
                    options={revenueChartOptions}
                    series={[{ name: 'Revenue', data: monthlyRevenue }]}
                  />
                </ChartCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <ChartCard title="Top Services">
                  {topServices.length > 0 ? (
                    <ReactApexChart
                      type="bar"
                      height={320}
                      options={serviceBarOptions}
                      series={[
                        {
                          name: 'Bookings',
                          data: topServices.map((s) => s.count),
                        },
                      ]}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320 }}>
                      <Typography variant="body2" color="text.disabled">
                        No services booked in {selectedYear}
                      </Typography>
                    </Box>
                  )}
                </ChartCard>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </>
  );
};

export default Reports;
